import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api/client.js';
import MarkdownField from '../../components/admin/MarkdownField.jsx';

const BASE = import.meta.env.VITE_API_BASE || '/api';
const MAX_UPLOAD_BYTES = 130 * 1024 * 1024;

const CN_DIGITS = '〇一二三四五六七八九';
// Year reads digit-by-digit (2026 → 二〇二六).
function toCnNum(n) {
  return String(n).split('').map(d => CN_DIGITS[parseInt(d)] || d).join('');
}

// Months (1–12) and days (1–31) read as cardinals (16 → 十六, 20 → 二十,
// 31 → 三十一), never digit-by-digit.
function toCnCardinal(n) {
  if (n < 10) return CN_DIGITS[n];
  if (n === 10) return '十';
  if (n < 20) return '十' + CN_DIGITS[n - 10];
  const tens = Math.floor(n / 10), ones = n % 10;
  return CN_DIGITS[tens] + '十' + (ones ? CN_DIGITS[ones] : '');
}
function formatDateEn(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const date = new Date(+y, +m - 1, +d);
  return `${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} · ${y}`;
}
function formatDateCn(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${toCnNum(y)}年${toCnCardinal(parseInt(m))}月${toCnCardinal(parseInt(d))}日`;
}
function formatDate(iso, lang) {
  return lang === 'en' ? formatDateEn(iso) : formatDateCn(iso);
}

const S = {
  back: {
    fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.2em', textTransform: 'uppercase',
    color: 'var(--bronze)', cursor: 'pointer', background: 'none', border: 'none',
    display: 'block', marginBottom: 20, padding: 0,
  },
  h1: { fontFamily: 'var(--display)', fontSize: 32, fontWeight: 400, margin: '0 0 28px' },
  label: {
    fontFamily: 'var(--label)', fontSize: 10, letterSpacing: '.2em', textTransform: 'uppercase',
    display: 'block', marginBottom: 4, color: 'var(--slate-70)',
  },
  input: {
    width: '100%', padding: '10px 12px',
    border: '1px solid var(--bronze-30)', background: 'var(--white)',
    fontFamily: 'var(--body)', fontSize: 16, color: 'var(--slate)',
    outline: 'none', marginBottom: 18,
  },
  textarea: {
    width: '100%', minHeight: 80, padding: 12,
    border: '1px solid var(--bronze-30)', background: 'var(--white)',
    fontFamily: 'var(--body)', fontSize: 16, color: 'var(--slate)',
    outline: 'none', marginBottom: 18, resize: 'vertical',
  },
  row: { display: 'flex', gap: 24 },
  col: { flex: 1, minWidth: 0 },
  saveBtn: {
    padding: '12px 32px', background: 'var(--slate)', color: 'var(--bone)',
    border: 0, fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.24em',
    textTransform: 'uppercase', cursor: 'pointer',
  },
  delBtn: {
    padding: '12px 32px', background: 'none', color: '#c44',
    border: '1px solid #c44', fontFamily: 'var(--label)', fontSize: 11,
    letterSpacing: '.24em', textTransform: 'uppercase', cursor: 'pointer',
    marginLeft: 12,
  },
  checkLabel: {
    fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.2em', textTransform: 'uppercase',
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18,
  },
  segBtn: (active) => ({
    padding: '8px 20px', cursor: 'pointer',
    fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.2em', textTransform: 'uppercase',
    background: active ? 'var(--slate)' : 'var(--white)',
    color: active ? 'var(--bone)' : 'var(--slate-70)',
    border: '1px solid var(--bronze-30)',
  }),
};

function UploadField({ value, onChange, label, accept }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      alert(
        `File is ${(file.size / 1024 / 1024).toFixed(1)} MB. ` +
        `The host caps uploads at ~130 MB. ` +
        `Please compress the video (HandBrake / ffmpeg) and try again.`
      );
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        alert(`Upload failed (HTTP ${res.status})\n${body.slice(0, 300)}`);
      } else {
        const json = await res.json().catch(() => null);
        if (json?.data?.url) onChange(json.data.url);
        else alert(`Upload returned 200 but no URL.\n${JSON.stringify(json).slice(0, 300)}`);
      }
    } catch (err) {
      alert(`Upload failed: ${err.message || err}`);
    }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div>
      <label style={S.label}>{label}</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <input style={{ ...S.input, marginBottom: 0, flex: 1 }}
          value={value || ''} onChange={e => onChange(e.target.value)} />
        <label style={{
          padding: '10px 18px', background: 'var(--bronze)', color: 'var(--white)',
          border: 0, fontFamily: 'var(--label)', fontSize: 10, letterSpacing: '.2em',
          textTransform: 'uppercase', cursor: uploading ? 'wait' : 'pointer',
          whiteSpace: 'nowrap', display: 'flex', alignItems: 'center',
        }}>
          {uploading ? 'Uploading…' : 'Upload'}
          <input type="file" accept={accept} onChange={handleUpload} style={{ display: 'none' }} />
        </label>
      </div>
    </div>
  );
}

const EMPTY = {
  lang: 'cn', type: 'video', title: '', intro: '', body_md: '',
  video_url: '', cover_image: '', auto_play: false, date: '', _dateIso: '', published: false,
};

export default function MediaEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(isNew ? { ...EMPTY } : null);

  useEffect(() => {
    if (isNew) { setForm({ ...EMPTY }); return; }
    // Use the api client (Bearer token + unmapped snake_case fields) so
    // unpublished drafts load and the form reads d.video_url / d.auto_play.
    api.mediaEdit(id)
      .then(d => {
        if (!d) return;
        const init = {
          lang: d.lang || 'cn',
          type: d.type || 'video',
          title: d.title || '',
          intro: d.intro || '',
          body_md: d.body_md || '',
          video_url: d.video_url || '',
          cover_image: d.cover_image || '',
          auto_play: !!d.auto_play,
          date: d.date || '',
          _dateIso: '',
          published: !!d.published,
        };
        const m = d.date?.match(/(\w+)\s*(\d+)?\s*·\s*(\d{4})/);
        if (m) {
          const months = { january:'01',february:'02',march:'03',april:'04',may:'05',june:'06',july:'07',august:'08',september:'09',october:'10',november:'11',december:'12' };
          const month = months[m[1]?.toLowerCase()] || '01';
          const day = (m[2] || '01').padStart(2, '0');
          init._dateIso = `${m[3]}-${month}-${day}`;
        }
        setForm(init);
      })
      .catch(() => {});
  }, [id]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const setLang = (lang) => setForm(prev => ({
    ...prev,
    lang,
    date: prev._dateIso ? formatDate(prev._dateIso, lang) : prev.date,
  }));

  const handleSave = async () => {
    const payload = {
      lang: form.lang,
      type: form.type,
      title: form.title,
      date: form.date,
      intro: form.intro,
      body_md: form.type === 'article' ? form.body_md : '',
      video_url: form.type === 'video' ? form.video_url : '',
      cover_image: form.cover_image,
      auto_play: form.type === 'video' ? !!form.auto_play : false,
      published: form.published,
    };
    try {
      if (isNew) await api.createMedia(payload);
      else await api.updateMedia(id, payload);
      navigate('/admin/media');
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this media item?')) return;
    try {
      await api.deleteMedia(id);
      navigate('/admin/media');
    } catch (e) { alert(e.message); }
  };

  if (!form) return null;

  return (
    <div>
      <button style={S.back} onClick={() => navigate('/admin/media')}>← Back</button>
      <h1 style={S.h1}>{isNew ? 'New Media Item' : 'Edit Media Item'}</h1>

      <div style={S.row}>
        <div style={S.col}>
          <label style={S.label}>Language</label>
          <div style={{ display: 'flex', marginBottom: 18 }}>
            <button type="button" style={S.segBtn(form.lang === 'cn')} onClick={() => setLang('cn')}>中文</button>
            <button type="button" style={S.segBtn(form.lang === 'en')} onClick={() => setLang('en')}>English</button>
          </div>
        </div>
        <div style={S.col}>
          <label style={S.label}>Type</label>
          <div style={{ display: 'flex', marginBottom: 18 }}>
            <button type="button" style={S.segBtn(form.type === 'video')} onClick={() => set('type', 'video')}>Video</button>
            <button type="button" style={S.segBtn(form.type === 'article')} onClick={() => set('type', 'article')}>Long-form</button>
          </div>
        </div>
      </div>

      <div>
        <label style={S.label}>Title</label>
        <input style={S.input} value={form.title} onChange={e => set('title', e.target.value)} />
      </div>

      <div>
        <label style={S.label}>Intro</label>
        <textarea style={S.textarea} value={form.intro} onChange={e => set('intro', e.target.value)} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={S.label}>Date</label>
        <input type="date" value={form._dateIso || ''}
          onChange={e => {
            const iso = e.target.value;
            set('_dateIso', iso);
            set('date', formatDate(iso, form.lang));
          }}
          style={{
            padding: '10px 12px',
            border: '1px solid var(--bronze-30)', background: 'var(--white)',
            fontFamily: 'var(--body)', fontSize: 16, color: 'var(--slate)',
            outline: 'none',
          }} />
        <span style={{ marginLeft: 18, fontFamily: 'var(--body)', fontSize: 15, color: 'var(--bronze)', letterSpacing: '.04em' }}>
          {form.date || '—'}
        </span>
      </div>

      {form.type === 'video' ? (
        <>
          <div style={S.row}>
            <div style={S.col}>
              <UploadField label="Video" accept="video/*,.mp4,.webm,.mov"
                value={form.video_url} onChange={v => set('video_url', v)} />
            </div>
            <div style={S.col}>
              <UploadField label="Cover Image" accept="image/*,.jpg,.png,.webp"
                value={form.cover_image} onChange={v => set('cover_image', v)} />
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={S.checkLabel}>
              <input type="checkbox" checked={!!form.auto_play} onChange={e => set('auto_play', e.target.checked)} />
              Auto-play video
            </label>
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: 18 }}>
            <UploadField label="Cover Image (optional)" accept="image/*,.jpg,.png,.webp"
              value={form.cover_image} onChange={v => set('cover_image', v)} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <MarkdownField label="Body" value={form.body_md} onChange={v => set('body_md', v)} />
          </div>
        </>
      )}

      <div style={{ marginBottom: 28, padding: '14px 16px', background: 'var(--bone-soft)', border: '1px solid var(--bronze-30)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--body)', fontSize: 16, color: 'var(--slate)', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.published} onChange={e => set('published', e.target.checked)} />
          <span><strong>{form.published ? 'Published' : 'Draft'}</strong> — {form.published ? 'visible on the public site' : 'hidden until you publish it'}</span>
        </label>
      </div>

      <div>
        <button style={S.saveBtn} onClick={handleSave}>Save</button>
        {!isNew && <button style={S.delBtn} onClick={handleDelete}>Delete</button>}
      </div>
    </div>
  );
}
